
import { useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Category } from "@/services/promptService";

interface UseExploreUrlSyncProps {
  selectedCategory: string | null;
  searchQuery: string;
  categories: Category[];
  initialCategoriesLoaded: React.MutableRefObject<boolean>;
  isCategoryProcessing: React.MutableRefObject<boolean>;
}

export function useExploreUrlSync({
  selectedCategory,
  searchQuery,
  categories,
  initialCategoriesLoaded,
  isCategoryProcessing
}: UseExploreUrlSyncProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const lastUpdateRef = useRef<number>(0);
  const lastParamsRef = useRef<string>("");
  const urlUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Create a memoized function to build URL params
  const buildUrlParams = useCallback(() => {
    const params = new URLSearchParams();
    
    if (selectedCategory && categories.length > 0) {
      const category = categories.find(cat => cat.id === selectedCategory);
      if (category) {
        params.set('category', category.slug);
      }
    }
    
    if (searchQuery) {
      params.set('q', searchQuery);
    }
    
    return params;
  }, [selectedCategory, searchQuery, categories]);

  // Update URL when filters change with debounce
  useEffect(() => {
    // Skip if not ready or processing
    if (!initialCategoriesLoaded.current || isCategoryProcessing.current) {
      return;
    }
    
    // Clear previous timeout
    if (urlUpdateTimeoutRef.current) {
      clearTimeout(urlUpdateTimeoutRef.current);
    }
    
    // Add a delay before updating the URL to reduce frequency
    urlUpdateTimeoutRef.current = setTimeout(() => {
      // Throttle URL updates - no more than once per 1.5 seconds
      const now = Date.now();
      if (now - lastUpdateRef.current < 1500) {
        return;
      }
      
      const params = buildUrlParams();
      const newSearch = params.toString();
      const currentSearch = location.search.replace(/^\?/, '');
      
      // Skip if params are the same
      if (newSearch === lastParamsRef.current) {
        return;
      }
      
      // Update the URL only if there's an actual change
      if (newSearch !== currentSearch) {
        console.log('Updating URL:', newSearch);
        lastUpdateRef.current = now;
        lastParamsRef.current = newSearch;
        
        navigate({
          pathname: location.pathname,
          search: newSearch || undefined
        }, { replace: true });
      }
    }, 500); // Add 500ms debounce delay
    
    return () => {
      if (urlUpdateTimeoutRef.current) {
        clearTimeout(urlUpdateTimeoutRef.current);
      }
    };
  }, [selectedCategory, searchQuery, navigate, location.pathname, categories, buildUrlParams, location.search]);
}
